import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, ChevronRightOutline, Flex, rem, Skeleton, Text, VStack } from '@bonusly/design-system';

import { useHashtags } from '../../../../components/recognition/bonus/hooks/use-hashtags';
import { LeaderboardFilters as LeaderboardFiltersType, useLeaderboard } from '../../../bonus-feed/features/recognition/api/hooks/leaderboards';
import { RecognitionCount } from '../../../bonus-feed/types/user';
import { useTimeframeDates } from './hooks/use-timeframe';
import { LeaderboardFilters } from './leaderboard-filters';
import { LeaderboardUserRow, LeaderboardUserRowSkeleton } from './leaderboard-user-row';
import { LeaderboardDirection, LeaderboardState, ScopeType, TimeframeOption } from './types';

interface LeaderboardInitialViewProps {
	state: LeaderboardState;
	onViewAll: (direction?: LeaderboardDirection) => void;
	onViewHashtag: (hashtag: string) => void;
	onScopeChange: (scopeType: ScopeType, teamId: string | null, departmentId: string | null) => void;
	onTimeframeChange: (timeframe: TimeframeOption) => void;
	onCustomDateChange: (startDate: string, endDate: string) => void;
}

const PREVIEW_LIMIT = 5;

// Helper to build scope filter params from state
const getScopeParams = (state: LeaderboardState) => {
	if (state.scopeType === 'team' && state.selectedTeam) {
		return { team: state.selectedTeam };
	}
	if (state.scopeType === 'department' && state.selectedDepartment) {
		return { department: state.selectedDepartment };
	}
	return {};
};

export const LeaderboardInitialView: FC<LeaderboardInitialViewProps> = ({
	state,
	onViewAll,
	onViewHashtag,
	onScopeChange,
	onTimeframeChange,
	onCustomDateChange,
}) => {
	const { t } = useTranslation('navigation');
	const { startDate, endDate } = useTimeframeDates(state.selectedTimeframe, state.customStartDate, state.customEndDate);

	// Fetch ALL company values (not just trending ones)
	const { data: hashtagData, isLoading: isLoadingHashtags } = useHashtags({
		params: { display_format: 'core_value_hashtags' },
	});

	// Map core values to the format we need (value field contains the hashtag)
	const companyValues = (hashtagData?.data?.result?.values ?? []).map((cv) => ({
		hashtag: cv.value.startsWith('#') ? cv.value : `#${cv.value}`,
		core_value: cv,
	}));

	// Memoize filter objects to prevent unnecessary API refetches
	const receivedFilters: LeaderboardFiltersType = useMemo(
		() => ({
			limit: PREVIEW_LIMIT,
			start_date: startDate,
			end_date: endDate,
			direction: 'received',
			...getScopeParams(state),
		}),
		[startDate, endDate, state.scopeType, state.selectedTeam, state.selectedDepartment]
	);

	const givenFilters: LeaderboardFiltersType = useMemo(
		() => ({
			limit: PREVIEW_LIMIT,
			start_date: startDate,
			end_date: endDate,
			direction: 'given',
			...getScopeParams(state),
		}),
		[startDate, endDate, state.scopeType, state.selectedTeam, state.selectedDepartment]
	);

	// Fetch all company leaderboard (received)
	const { data: allCompanyData, isLoading: isLoadingAllCompany, isFetching: isFetchingAllCompany } = useLeaderboard(receivedFilters);

	// Fetch top givers leaderboard
	const { data: topGiversData, isLoading: isLoadingTopGivers, isFetching: isFetchingTopGivers } = useLeaderboard(givenFilters);

	const allCompanyUsers = allCompanyData?.users ?? [];
	const showAllCompanySkeleton = isLoadingAllCompany || isFetchingAllCompany;

	const topGiversUsers = topGiversData?.users ?? [];
	const showTopGiversSkeleton = isLoadingTopGivers || isFetchingTopGivers;

	return (
		<VStack spacing={0} align="stretch" px={rem(12)}>
			{/* Filters */}
			<LeaderboardFilters state={state} onScopeChange={onScopeChange} onTimeframeChange={onTimeframeChange} onCustomDateChange={onCustomDateChange} />

			{/* Most Recognized Section */}
			<Box mt={rem(16)}>
				<LeaderboardSection
					title={t('leaderboard.allCompany')}
					isLoading={showAllCompanySkeleton}
					users={allCompanyUsers}
					onViewAll={() => onViewAll('received')}
					hashtag={null}
				/>
			</Box>

			{/* Top Givers Section */}
			<Box mt={rem(16)}>
				<LeaderboardSection
					title={t('leaderboard.topGivers')}
					isLoading={showTopGiversSkeleton}
					users={topGiversUsers}
					onViewAll={() => onViewAll('given')}
					hashtag={null}
					direction="given"
				/>
			</Box>

			{/* Company Values Sections */}
			{!isLoadingHashtags &&
				companyValues.map((value) => (
					<Box key={value.hashtag} mt={rem(16)}>
						<LeaderboardHashtagSection hashtag={value.hashtag} state={state} startDate={startDate} endDate={endDate} onViewHashtag={onViewHashtag} />
					</Box>
				))}

			{isLoadingHashtags && (
				<Box mt={rem(16)}>
					<Skeleton height={rem(200)} borderRadius="md" />
				</Box>
			)}
		</VStack>
	);
};

interface LeaderboardSectionProps {
	title: string;
	isLoading: boolean;
	users: RecognitionCount[];
	onViewAll: () => void;
	hashtag: string | null;
	/** Direction of recognitions - 'received' or 'given'. Defaults to 'received'. */
	direction?: LeaderboardDirection;
}

const LeaderboardSection: FC<LeaderboardSectionProps> = ({ title, isLoading, users, onViewAll, hashtag, direction = 'received' }) => {
	const { t } = useTranslation('navigation');

	return (
		<Box>
			{/* Section Header */}
			<Flex justifyContent="space-between" alignItems="center" gap={rem(12)} h={rem(24)}>
				<Text
					as="button"
					fontSize="md"
					fontWeight="semibold"
					color="text.default"
					lineHeight="24px"
					flex={1}
					textAlign="left"
					bg="transparent"
					border="none"
					p={0}
					cursor="pointer"
					onClick={onViewAll}
					_hover={{ opacity: 0.7 }}
				>
					{title}
				</Text>
				<Button
					variant="link"
					size="sm"
					rightIcon={<ChevronRightOutline boxSize={rem(16)} />}
					onClick={onViewAll}
					color="text.softest"
					fontWeight="medium"
					fontSize="sm"
					p={0}
					h="auto"
					minW="auto"
					_hover={{ textDecoration: 'none', color: 'text.soft' }}
				>
					{t('leaderboard.seeAll')}
				</Button>
			</Flex>

			{/* User List */}
			{isLoading ? (
				<VStack as="ul" spacing={0} align="stretch" listStyleType="none" m={0} p={0} mt={1}>
					{Array.from({ length: 5 }).map((_, i) => (
						<LeaderboardUserRowSkeleton key={i} />
					))}
				</VStack>
			) : users.length > 0 ? (
				<VStack as="ul" spacing={0} align="stretch" listStyleType="none" m={0} p={0} mt={1}>
					{users.slice(0, PREVIEW_LIMIT).map((user, index) => (
						<LeaderboardUserRow key={user.id} record={user} rank={index + 1} hashtag={hashtag} direction={direction} />
					))}
				</VStack>
			) : (
				<Text fontSize="sm" color="text.softer" py={rem(16)} textAlign="center" mt={1}>
					{t('leaderboard.noResults')}
				</Text>
			)}
		</Box>
	);
};

interface LeaderboardHashtagSectionProps {
	hashtag: string;
	state: LeaderboardState;
	startDate: string;
	endDate: string;
	onViewHashtag: (hashtag: string) => void;
}

const LeaderboardHashtagSection: FC<LeaderboardHashtagSectionProps> = ({ hashtag, state, startDate, endDate, onViewHashtag }) => {
	const { t } = useTranslation('navigation');

	// Remove # prefix for API call
	const hashtagValue = hashtag.startsWith('#') ? hashtag.slice(1) : hashtag;

	const { data, isLoading, isFetching } = useLeaderboard({
		hashtag: hashtagValue,
		limit: PREVIEW_LIMIT,
		start_date: startDate,
		end_date: endDate,
		...getScopeParams(state),
	});

	const users = data?.users ?? [];
	const showSkeleton = isLoading || isFetching;

	return (
		<Box>
			{/* Section Header */}
			<Flex justifyContent="space-between" alignItems="center" gap={rem(12)} h={rem(24)}>
				<Text
					as="button"
					fontSize="md"
					fontWeight="semibold"
					color="text.default"
					lineHeight="24px"
					flex={1}
					textAlign="left"
					bg="transparent"
					border="none"
					p={0}
					cursor="pointer"
					onClick={() => onViewHashtag(hashtagValue)}
					_hover={{ opacity: 0.7 }}
				>
					{hashtag}
				</Text>
				<Button
					variant="link"
					size="sm"
					rightIcon={<ChevronRightOutline boxSize={rem(16)} />}
					onClick={() => onViewHashtag(hashtagValue)}
					color="text.softest"
					fontWeight="medium"
					fontSize="sm"
					p={0}
					h="auto"
					minW="auto"
					_hover={{ textDecoration: 'none', color: 'text.soft' }}
				>
					{t('leaderboard.seeAll')}
				</Button>
			</Flex>

			{/* User List */}
			{showSkeleton ? (
				<VStack as="ul" spacing={0} align="stretch" listStyleType="none" m={0} p={0} mt={1}>
					{Array.from({ length: 5 }).map((_, i) => (
						<LeaderboardUserRowSkeleton key={i} />
					))}
				</VStack>
			) : users.length > 0 ? (
				<VStack as="ul" spacing={0} align="stretch" listStyleType="none" m={0} p={0} mt={1}>
					{users.slice(0, PREVIEW_LIMIT).map((user, index) => (
						<LeaderboardUserRow key={user.id} record={user} rank={index + 1} hashtag={hashtag} />
					))}
				</VStack>
			) : (
				<Text fontSize="sm" color="text.softer" py={rem(16)} textAlign="center" mt={1}>
					{t('leaderboard.noResults')}
				</Text>
			)}
		</Box>
	);
};
